import { OptionallyVisible } from 'components';
import { useNotifications } from 'providers/notifications/useNotifications';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router';
import { useParams } from 'react-router-dom';
import { useRetryPayoutMutation } from 'services/payouts';

import { Panel } from 'components/containers';

import RoutePath from 'router/path';

import { selectUserLevel } from 'state/slices/userSlice';

import { NotificationSeverity } from 'types/notifications';
import { UserAccessLevel } from 'types/user';

import Logger from 'utils/logger';

import { PayoutStatusBadge } from '../../../components/payoutStatusBadge/payoutStatusBadge';
import { KEYS, LABELS } from '../../keys';
import { PayoutSectionProps } from '../../types';
import {
  ColumnFlexWithGap,
  ErrorText,
  Item,
  ItemName,
  ItemValue,
  OnChainTransactionHash,
  RetryButton,
  RowFlexWithGap,
  SectionWrapper,
} from '../styles';

export const DestinationSection = ({ payoutData }: PayoutSectionProps) => {
  const { t } = useTranslation();
  const { pushToast } = useNotifications();
  const [retryPayout, { isLoading }] = useRetryPayoutMutation();
  const navigate = useNavigate();
  const { accountUuid } = useParams();
  const userLevel = useSelector(selectUserLevel);

  const { destination } = payoutData;

  const isBankTransaction = Boolean(destination.bankAccount);
  const isChainTransaction = Boolean(destination.crypto);
  const hasMessage = Boolean(destination.bankAccount?.memo);
  const hasLevelToRetryPayout = Boolean(userLevel && userLevel >= UserAccessLevel.Admin);
  const canRetryPayout = payoutData.isRetryable && hasLevelToRetryPayout;

  const onRetryClick = async () => {
    if (!accountUuid) {
      return;
    }

    const retryResponse = await retryPayout({ accountUuid, payoutUuid: payoutData.uuid });
    if ('error' in retryResponse) {
      pushToast({
        severity: NotificationSeverity.error,
        message: t(LABELS.ERRORS.RETRY_FAILED),
      });
      return;
    }

    const nextPayoutUuid = retryResponse.data?.nextPayoutUuid;
    if (nextPayoutUuid) {
      navigate(RoutePath.pwc.payoutById(accountUuid, nextPayoutUuid));
      return;
    }

    Logger.warn('User was not redirected to the new payout because nextPayoutUuid is missing');
  };

  return (
    <Panel label={t(LABELS.SECTIONS.DESTINATION.TITLE)}>
      <SectionWrapper>
        <Item>
          <ItemName>{t(LABELS.SECTIONS.DESTINATION.PAYMENT_RAIL)}</ItemName>
          <ItemValue>{destination.paymentRail?.toUpperCase() || KEYS.DASH}</ItemValue>
        </Item>

        <Item>
          <ItemName>{t(LABELS.SECTIONS.DESTINATION.STATUS)}</ItemName>
          <ItemValue>
            <ColumnFlexWithGap>
              <RowFlexWithGap>
                <div>
                  <PayoutStatusBadge status={payoutData.status} />
                </div>
                <OptionallyVisible visible={canRetryPayout}>
                  <RetryButton secondary isLoading={isLoading} onClick={onRetryClick}>
                    {t(LABELS.SECTIONS.DESTINATION.ACTIONS.RETRY)}
                  </RetryButton>
                </OptionallyVisible>
              </RowFlexWithGap>
              <OptionallyVisible visible={Boolean(payoutData.statusReason)}>
                <ErrorText>{payoutData.statusReason}</ErrorText>
              </OptionallyVisible>
            </ColumnFlexWithGap>
          </ItemValue>
        </Item>

        <OptionallyVisible visible={isChainTransaction}>
          <Item>
            <ItemName>{t(LABELS.SECTIONS.DESTINATION.DESTINATION_WALLET)}</ItemName>
            <ItemValue>{payoutData?.destination.crypto?.wallet}</ItemValue>
          </Item>
          <Item>
            <ItemName>{t(LABELS.SECTIONS.DESTINATION.TRANSACTION_HASH)}</ItemName>
            <OnChainTransactionHash displayFullHash>
              <a href={payoutData.destination?.crypto?.onChain?.explorerUrl} target="_blank">
                {payoutData.destination?.crypto?.onChain?.transactionHash}
              </a>
            </OnChainTransactionHash>
          </Item>
        </OptionallyVisible>

        <OptionallyVisible visible={isBankTransaction}>
          <Item>
            <ItemName>{t(LABELS.SECTIONS.DESTINATION.BANK_ACCOUNT)}</ItemName>
            <ItemValue>{payoutData.destination?.bankAccount?.name}</ItemValue>
          </Item>

          <Item>
            <ItemName>{t(LABELS.SECTIONS.DESTINATION.TRACE_ID)}</ItemName>
            <ItemValue>{payoutData.destination?.bankAccount?.traceId}</ItemValue>
          </Item>
        </OptionallyVisible>

        <OptionallyVisible visible={hasMessage}>
          <Item>
            <ItemName>{t(LABELS.SECTIONS.DESTINATION.MESSAGE)}</ItemName>
            <ItemValue>{destination.bankAccount?.memo}</ItemValue>
          </Item>
        </OptionallyVisible>
      </SectionWrapper>
    </Panel>
  );
};
